import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, Buildings, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { money } from "@/lib/money";


export default function GeneratePreviewModal({ onClose, onGenerated }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/invoices/generate/preview")
      .then((r) => setPreview(r?.data || null))
      .finally(() => setLoading(false));
  }, []);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/invoices/generate", {});
      toast.success(`Created ${data.created} draft invoice${data.created === 1 ? "" : "s"}.`);
      onGenerated?.(data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || e.message);
    } finally {
      setBusy(false);
    }
  };

  const nothing = !loading && preview && preview.invoice_count === 0;

  return (
    <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50 overflow-y-auto"
         data-testid="generate-preview-modal" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-2xl p-6 border border-slate-200 my-8"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="tinylabel">Review before commit</div>
            <h2 className="text-xl font-black">Generate invoices from approved timesheets</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"><X size={22} /></button>
        </div>

        {loading && <div className="py-10 text-center text-slate-400">Loading preview…</div>}

        {nothing && (
          <div className="py-8 text-center">
            <Warning size={40} className="mx-auto text-amber-500" />
            <div className="mt-3 font-bold text-slate-900">No approved timesheets waiting.</div>
            <div className="text-sm text-slate-500 mt-1">
              Every approved timesheet has already been invoiced, or none have been approved yet this week.
            </div>
          </div>
        )}

        {!loading && preview && preview.invoice_count > 0 && (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
                <div className="tinylabel">Draft invoices</div>
                <div className="text-2xl font-black mt-1" data-testid="preview-invoice-count">{preview.invoice_count}</div>
                <div className="text-xs text-slate-500 mt-0.5">across {preview.client_count} client{preview.client_count === 1 ? "" : "s"}</div>
              </div>
              <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
                <div className="tinylabel">Timesheets</div>
                <div className="text-2xl font-black mt-1">{preview.total_timesheets}</div>
                <div className="text-xs text-slate-500 mt-0.5">will flip to invoiced</div>
              </div>
              <div className="border border-emerald-200 rounded-md p-3 bg-emerald-50">
                <div className="tinylabel text-emerald-700">Total billable</div>
                <div className="text-2xl font-black mt-1 text-emerald-700 font-mono" data-testid="preview-total-billable">{money(preview.total_billable)}</div>
              </div>
            </div>

            <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
              {preview.clients.map((c) => (
                <div key={c.client_id} className="border border-slate-200 rounded-md p-3" data-testid={`preview-client-${c.client_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Buildings size={18} weight="duotone" className="text-slate-600" />
                      <div>
                        <div className="font-bold text-slate-900">{c.client_name} <span className="font-mono text-xs text-slate-400">({c.client_initials})</span></div>
                        <div className="text-xs text-slate-500">Terms: <b>{c.payment_terms}</b> · {c.invoices.length} invoice{c.invoices.length === 1 ? "" : "s"}</div>
                      </div>
                    </div>
                    <div className="text-right font-mono font-bold text-slate-900">{money(c.client_total)}</div>
                  </div>
                  <div className="mt-2 pl-6 space-y-1">
                    {c.invoices.map((inv) => (
                      <div key={inv.project_id} className="flex items-start justify-between text-xs text-slate-600">
                        <div>
                          <span className="font-semibold text-slate-800">{inv.project_name}</span>
                          {inv.po_number ? <span className="text-slate-400 ml-1">· PO {inv.po_number}</span> : null}
                          <div className="text-slate-500 mt-0.5">
                            {inv.timesheet_count} timesheet{inv.timesheet_count === 1 ? "" : "s"} · {inv.worker_count} worker{inv.worker_count === 1 ? "" : "s"}
                            {inv.worker_names.length ? <span className="text-slate-400"> ({inv.worker_names.join(", ")})</span> : null}
                          </div>
                        </div>
                        <div className="font-mono">{money(inv.amount)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="mt-6 flex gap-3 justify-end border-t border-slate-100 pt-4">
          <button type="button" onClick={onClose}
                  data-testid="preview-cancel-btn"
                  className="h-11 px-4 border border-slate-300 rounded-md font-semibold">Cancel</button>
          {!nothing && (
            <button onClick={confirm} disabled={busy || loading || !preview?.invoice_count}
                    data-testid="preview-confirm-btn"
                    className="h-11 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md disabled:opacity-60">
              {busy ? "Creating…" : `Create ${preview?.invoice_count || 0} draft invoice${preview?.invoice_count === 1 ? "" : "s"}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
